<template>
  <v-form v-if="data" :readonly="readonly" ref="form">
    <v-row>
      <v-col>
        <HelpCard v-if="!logView" class="mt-2" caption="Показание препарата" open>
          <v-alert v-if="about.error" type="error" outlined dense class="mb-3">
            {{ about.error }}
          </v-alert>
          <v-alert
            v-else-if="about.data?.about_ready && about.data.condition"
            type="info"
            outlined
            dense
            class="text-caption mb-4"
          >
            <span class="font-weight-medium">Показание из «Информации о препарате»:</span>
            <span class="text-pre-wrap d-block mt-1">{{ about.data.condition }}</span>
          </v-alert>
          <v-alert v-else-if="about.data" type="warning" outlined dense class="mb-4">
            Ожидание сводной версии «Информации о препарате» (см. показание
            <b>ГРЛС</b> / <b>ЕАЭС</b>)
          </v-alert>
          <div class="text-caption font-italic primary--text mb-0">
            Внесите показания предлагаемого ЛП (в каждой вкладке отдельно
            показание)
          </div>
        </HelpCard>
      </v-col>
    </v-row>

    <v-row v-if="data.indications.length">
      <v-col>
        <div class="text-subtitle-2 font-weight-bold mb-2">
          Рекомендации агентств
        </div>
        <div class="d-flex align-center agency-indication-tabs">
          <v-tabs
            v-model="activeIndication"
            show-arrows
            @change="onIndicationChange"
          >
            <v-tab
              v-for="(ind, indIndex) in data.indications"
              :key="indIndex"
              class="agency-indication-tab"
            >
              <span class="agency-indication-tab__label">
                {{ indicationTabLabel(ind, indIndex) }}
              </span>
              <span
                v-if="!readonly && data.indications.length > 1"
                class="agency-indication-tab__close-wrap"
                @mousedown.stop.prevent
                @click.stop
              >
                <btn-with-confirm
                  icon
                  x-small
                  class="agency-indication-tab__close"
                  color="grey darken-1"
                  icon-main="mdi-close"
                  icon-x-small
                  title="Удалить показание"
                  @click.stop="removeIndication(indIndex)"
                  @mousedown.native.stop.prevent
                />
              </span>
            </v-tab>
          </v-tabs>
          <v-btn
            v-if="!readonly"
            color="accent"
            fab
            x-small
            class="ml-1 agency-indication-tabs__add"
            title="Добавить показание"
            @click="addIndication"
          >
            <v-icon small>mdi-plus</v-icon>
          </v-btn>
        </div>

        <v-tabs-items v-model="activeIndication" class="mb-2">
          <v-tab-item
            v-for="(ind, indIndex) in data.indications"
            :key="indIndex"
            class="pt-2"
          >
            <v-card outlined class="agency-indication-panel">
              <v-card-text class="pb-2">
                <v-textarea
                  v-model="ind.text"
                  :readonly="readonly"
                  :outlined="!readonly"
                  label="Показание"
                  rows="5"
                  auto-grow
                  no-resize
                  hide-details
                  class="mb-3"
                  @input="doChange()"
                  @blur="doBlur()"
                />
                <v-tabs v-model="activeAgencyTab" @change="doBlur()">
              <v-tab
                v-for="(agency, agencyIndex) in ind.agencies"
                :key="agencyIndex"
              >
                {{ agency.name }}
              </v-tab>
              <v-tab-item
                v-for="(agency, agencyIndex) in ind.agencies"
                :key="agencyIndex"
                class="pa-2"
              >
                <v-card
                  v-for="(item, index) in agency.data"
                  :key="index"
                  class="my-2"
                >
              <v-card-title class="py-1">
                <v-container
                  class="pa-0"
                  v-if="agency.data && agency.data.length > 1"
                >
                  <v-row dense no-gutters>
                    <v-col class="font-weight-bold">{{ index + 1 }}.</v-col>
                    <v-col md="1" v-if="!readonly" class="text-right">
                      <btn-with-confirm
                        icon
                        small
                        elevation="0"
                        color="warning"
                        icon-main="mdi-close"
                        icon-small
                        :title="`Удалить заключение ${agency.name}`"
                        @click="showDeleteItemDlg(agencyIndex, index)"
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-title>
              <v-card-text>
                <v-textarea
                  label="Оригинал заключения"
                  v-model="item.original"
                  auto-grow
                  @input="doChange()"
                  @blur="doBlur()"
                />
                <v-textarea
                  label="Перевод"
                  v-model="item.translate"
                  auto-grow
                  hint="В заключение переносится только перевод"
                  persistent-hint
                  @input="doChange()"
                  @blur="doBlur()"
                />
                <SelectDate
                  v-model="item.public_date"
                  label="Дата публикации"
                  ref="date"
                  @input="doChange(true)"
                  :disabled="readonly"
                />
                <ArrangeLink
                  v-model="item.link"
                  :link="readonly"
                  @change="doChange()"
                  @blur="doBlur()"
                />
              </v-card-text>
                </v-card>
                <div v-if="!readonly" class="text-right">
                  <v-btn
                    color="accent"
                    fab
                    small
                    @click="addItem(agencyIndex)"
                    :title="`Добавить заключение ${agency.name}`"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </div>
              </v-tab-item>
                </v-tabs>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>

    <ExpAgencyDeleteDialog
      v-if="data && !readonly"
      title="Подтверждение удаления"
      :lb="`Вы действительно хотите удалить ${
        delete_dialog.index + 1
      } заключение агенства ${deleteAgencyName}`"
      :name="delete_text"
      :dialog="delete_dialog.show"
      @onClose="delete_dialog.show = false"
      @onAction="deleteItem()"
    />
  </v-form>
</template>

<style scoped>
.v-card__title {
  font-size: 14px;
}
.agency-indication-tabs {
  width: fit-content;
  max-width: 100%;
}
.agency-indication-tabs >>> .v-tabs {
  flex: 0 1 auto;
  width: auto;
}
.agency-indication-tabs__add {
  flex-shrink: 0;
}
.agency-indication-tab {
  display: inline-flex !important;
  align-items: center;
  gap: 2px;
  text-transform: none;
  letter-spacing: normal;
  min-width: 0;
  max-width: 220px;
}
.agency-indication-tab >>> .v-btn {
  margin-right: -4px;
}
.agency-indication-tab__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 160px;
}
.agency-indication-tab__close-wrap {
  display: inline-flex;
  flex-shrink: 0;
}
.agency-indication-tab__close {
  flex-shrink: 0;
  opacity: 0.65;
}
.agency-indication-tab:hover .agency-indication-tab__close,
.agency-indication-tab.v-tab--active .agency-indication-tab__close {
  opacity: 1;
}
</style>

<script>
import { copyObject } from '@/lib/objects'
import {
  DEFAULT_AGENCY_CONCLUSION,
  DEFAULT_INDICATION,
  EX_AGENCIES_DEFAULT,
  indicationTabLabel,
  normalizeAgenciesExpertise,
} from './const'
import { SUB_EX_ABOUT } from '@/store/const/expertise'
import ArrangeLink from '@/components/editors/ArrangeLink'
import HelpCard from '@/components/HelpCard'
import SelectDate from '@/components/editors/SelectDate.vue'
import ExpAgencyDeleteDialog from '@/components/dialogs/ExpAgencyDeleteDialog'
import BtnWithConfirm from '@/components/buttons/btnWithConfirm'
import { mapActions } from 'vuex'

export default {
  name: 'Exp-Agency',
  components: {
    ArrangeLink,
    HelpCard,
    ExpAgencyDeleteDialog,
    SelectDate,
    BtnWithConfirm,
  },
  props: {
    value: { type: Object },
    readonly: { type: Boolean, default: false },
    onCreate: { type: Function, default: null },
    logView: { type: Boolean, default: false },
  },
  data: () => ({
    data: null,
    activeIndication: 0,
    activeAgencyTab: 0,
    delete_dialog: {
      show: false,
      agency: null,
      index: null,
    },
    about: {
      loading: false,
      error: '',
      data: null,
    },
  }),
  watch: {
    value: 'reInit',
    readonly: 'reInit',
  },
  async created() {
    if (this.readonly) {
      this.data = normalizeAgenciesExpertise(this.value)
    } else if (this.value) {
      this.data = normalizeAgenciesExpertise(copyObject(this.value))
    } else {
      this.data = null
    }

    if (!this.logView) {
      await this.loadAbout()
    }

    if (!this.data && this.onCreate) {
      this.data = normalizeAgenciesExpertise(
        await this.onCreate(this.initNewData())
      )
    }

    this.prefillFromAbout()
    this.clampActiveIndices()
  },
  computed: {
    activeIndicationAgencies() {
      return this.data?.indications?.[this.activeIndication]?.agencies ?? []
    },
    delete_text() {
      const agency = this.activeIndicationAgencies[this.delete_dialog?.agency]
      const item = agency?.data?.[this.delete_dialog?.index]
      if (!item?.original) return ''
      const s = String(item.original)
      return (s.length > 100 ? `${s.substring(0, 100)}...` : s) || ''
    },
    deleteAgencyName() {
      return (
        this.activeIndicationAgencies[this.delete_dialog?.agency]?.name || ''
      )
    },
  },
  mounted() {
    this.reInit()
  },
  methods: {
    indicationTabLabel,
    ...mapActions(['GET_PLP_REPORT']),

    prefillFromAbout() {
      const condition = this.about.data?.condition
      const first = this.data?.indications?.[0]
      if (condition && first && !String(first.text || '').trim()) {
        first.text = condition
      }
    },
    clampActiveIndices() {
      const indLen = this.data?.indications?.length || 0
      if (this.activeIndication >= indLen) {
        this.activeIndication = Math.max(0, indLen - 1)
      }
    },
    onIndicationChange() {
      this.activeAgencyTab = 0
      this.doBlur()
    },
    reInit() {
      if (this.readonly) {
        this.data = normalizeAgenciesExpertise(this.value)
      } else if (this.value) {
        this.data = normalizeAgenciesExpertise(copyObject(this.value))
      }
      this.clampActiveIndices()
      if (!this.readonly) {
        this.$nextTick().then(() => this.$refs.form?.validate())
      }
    },
    initNewData() {
      return copyObject(EX_AGENCIES_DEFAULT())
    },
    doChange(force = false) {
      if (!this.readonly) this.$emit('change', this.data, force)
    },
    doBlur() {
      this.$emit('blur')
    },
    addIndication() {
      this.data.indications.push(DEFAULT_INDICATION())
      this.activeIndication = this.data.indications.length - 1
      this.activeAgencyTab = 0
      this.doChange(true)
    },
    removeIndication(index) {
      const idx =
        typeof index === 'number' ? index : this.activeIndication
      this.data.indications.splice(idx, 1)
      this.clampActiveIndices()
      this.activeAgencyTab = 0
      this.doChange(true)
    },
    addItem(agencyIndex) {
      const agency = this.activeIndicationAgencies[agencyIndex]
      if (!agency) return
      agency.data.push(copyObject(DEFAULT_AGENCY_CONCLUSION()))
      this.doChange()
    },
    showDeleteItemDlg(agency, index) {
      this.delete_dialog = { show: true, agency, index }
    },
    deleteItem() {
      const { agency, index } = this.delete_dialog
      const list = this.activeIndicationAgencies[agency]?.data
      if (list) list.splice(index, 1)
      this.delete_dialog = { show: false, agency: null, index: null }
      this.doChange(true)
    },
    async loadAbout() {
      this.about = { loading: true, data: null, error: '' }
      try {
        const { expertise } = await this.GET_PLP_REPORT([SUB_EX_ABOUT])
        const about_ready = !!expertise?.about
        const condition = expertise?.about?.condition || ''
        this.about.data = { about_ready, condition }
      } catch (err) {
        this.about.error = err.message + (err.error ? ' ' + err.error : '')
        throw err
      } finally {
        this.about.loading = false
      }
    },
  },
}
</script>
